"use client"
import { useEffect, useMemo, useState } from "react"
import {
  type ResolvedBranchNode,
  PLAN_NODE_IDS,
} from "@/lib/branchTypes"
import { buildPrimaryChain } from "../data/playbookStateUtils"
import { PlaybookBranchNodeMarkdownContent } from "./PlaybookBranchNodeMarkdownContent"
import { PLAN_PRERENDER_WARMUP_ATTR } from "./planPrerenderWarmupScan"

// Warm every plan's content in the background so selecting or hovering one is
// fast. Picking (or hovering) a plan mounts its primary chain through
// PlaybookBranchNodeMarkdownContent — parsing each node's markdown and building
// a large Markdown element tree. That first mount is the lag the user sees.
//
// This component mounts those same trees once, ahead of time, in a hidden
// container, so by the time the reader acts the browser has already: parsed and
// cached every plan's markdown (getPlaybookBranchNodeChunks memoizes by
// content, and both the hover preview and the real selection read that cache),
// fetched/decoded the web fonts, and warmed the style/layout paths for this
// content shape. The subsequent hover/select then renders from warm caches
// instead of cold.
//
// Order D → C → B → A → S: the racier plans are the common first click off the
// choice point, so warm them first.
const WARMUP_PLAN_ORDER = ["D", "C", "B", "A", "S"] as const

type PlanPrerenderWarmupProps = {
  nodes: Record<string, ResolvedBranchNode>
}

export const PlanPrerenderWarmup = ({ nodes }: PlanPrerenderWarmupProps) => {
  const planChains = useMemo(
    () =>
      WARMUP_PLAN_ORDER.map((planId) => ({
        planId,
        chain: buildPrimaryChain(PLAN_NODE_IDS[planId], nodes),
      })).filter(({ chain }) => chain.length > 0),
    [nodes],
  )

  // How many plans have been added to the hidden tree so far. Starts at 0 (the
  // visible playbook renders unencumbered), then grows one plan per idle/frame
  // tick so the warmup mounts never coalesce into a single long, janky task.
  const [warmedCount, setWarmedCount] = useState(0)

  useEffect(() => {
    if (warmedCount >= planChains.length) return
    // Prefer requestIdleCallback so a warmup mount only runs when the main
    // thread is free; fall back to two rAFs (past the first paint) elsewhere.
    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void) => number
      cancelIdleCallback?: (id: number) => void
    })
    if (idle.requestIdleCallback && idle.cancelIdleCallback) {
      const id = idle.requestIdleCallback(() => setWarmedCount((c) => c + 1))
      return () => idle.cancelIdleCallback!(id)
    }
    let innerRafId = 0
    const outerRafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => setWarmedCount((c) => c + 1))
    })
    return () => {
      cancelAnimationFrame(outerRafId)
      cancelAnimationFrame(innerRafId)
    }
  }, [warmedCount, planChains.length])

  if (warmedCount === 0) return null

  return (
    <div
      {...{ [PLAN_PRERENDER_WARMUP_ATTR]: "true" }}
      aria-hidden="true"
      // display:none keeps the subtree mounted-and-warm but out of layout, out
      // of every global DOM scan, and out of the accessibility tree — so it
      // can't pollute the HUD date, legend, years rail, or scroll lock (all of
      // which also exclude this subtree by attribute; see
      // planPrerenderWarmupScan.ts). Rendered without a proseClassName so it
      // never matches the feedback layer's .primary-branch-prose selector.
      style={{ display: "none" }}
    >
      {planChains.slice(0, warmedCount).map(({ planId, chain }) => (
        <div key={planId}>
          {chain.map((nodeId) => {
            const node = nodes[nodeId]
            if (!node) return null
            return (
              <PlaybookBranchNodeMarkdownContent
                key={nodeId}
                nodeId={nodeId}
                node={node}
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}
